/**
 * IndexedDB dla offline replica i outbox.
 * Migracja z pending_expenses do nowej struktury.
 */

const DB_NAME = "HerdManagementDB";
const DB_VERSION = 3; // Bump z 2 na 3 (adding tag_verification_session)
const REPLICA_STORE = "replica";
const OUTBOX_STORE = "outbox";
const TAG_VERIFICATION_STORE = "tag_verification_session";
const LEGACY_EXPENSES_STORE = "pending_expenses";

export type DBConnection = {
  db: IDBDatabase;
  close: () => void;
};

export function openDb(): Promise<DBConnection | null> {
  if (!window.indexedDB) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error("[offline/db] Failed to open IndexedDB:", request.error);
      resolve(null);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const oldVersion = event.oldVersion;

      // Store: replica (key-value)
      if (!db.objectStoreNames.contains(REPLICA_STORE)) {
        const replicaStore = db.createObjectStore(REPLICA_STORE, { keyPath: "key" });
        replicaStore.createIndex("timestamp", "timestamp", { unique: false });
      }

      // Store: outbox (queue mutacji)
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        const outboxStore = db.createObjectStore(OUTBOX_STORE, {
          keyPath: "id",
          autoIncrement: true,
        });
        outboxStore.createIndex("status", "status", { unique: false });
        outboxStore.createIndex("created_at", "created_at", { unique: false });
      }

      // Store: tag_verification_session (offline weryfikacja)
      if (!db.objectStoreNames.contains(TAG_VERIFICATION_STORE)) {
        db.createObjectStore(TAG_VERIFICATION_STORE, { keyPath: "id" });
      }

      // Migracja: pending_expenses → outbox
      if (oldVersion < 2 && db.objectStoreNames.contains(LEGACY_EXPENSES_STORE)) {
        migrateLegacyExpenses(event, db);
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      resolve({
        db,
        close: () => db.close(),
      });
    };

    request.onblocked = () => {
      console.warn("[offline/db] IndexedDB upgrade blocked");
      reject(new Error("IndexedDB upgrade blocked"));
    };
  });
}

function migrateLegacyExpenses(event: IDBVersionChangeEvent, db: IDBDatabase): void {
  const transaction = (event.target as IDBOpenDBRequest).transaction;
  if (!transaction) return;

  const legacyStore = transaction.objectStore(LEGACY_EXPENSES_STORE);
  const outboxStore = transaction.objectStore(OUTBOX_STORE);

  const getAllRequest = legacyStore.getAll();

  getAllRequest.onsuccess = () => {
    const expenses = getAllRequest.result as Array<{
      id: number;
      type?: string;
      amount?: string | number;
      date?: string;
      category?: string;
      subcategory?: string | null;
      note?: string | null;
      assign?: string | null;
      assign_tags?: string[];
      assign_herd?: boolean;
      has_photo?: boolean;
      client_id?: string;
    }>;

    for (const expense of expenses) {
      const clientId = expense.client_id || `legacy-expense-${expense.id}`;
      const outboxEntry = {
        client_id: clientId,
        method: "POST" as const,
        path: "/api/finance/transactions",
        body: {
          type: expense.type || "expense",
          amount: expense.amount,
          date: expense.date,
          category: expense.category,
          subcategory: expense.subcategory || null,
          note: expense.note || null,
          assign: expense.assign || null,
          assign_tags: Array.isArray(expense.assign_tags) ? expense.assign_tags : [],
          assign_herd: Boolean(expense.assign_herd),
          has_photo: Boolean(expense.has_photo),
          client_id: clientId,
        },
        status: "pending",
        created_at: Date.now(),
        retry_count: 0,
      };
      outboxStore.add(outboxEntry);
    }

    // Usuń legacy store
    if (db.objectStoreNames.contains(LEGACY_EXPENSES_STORE)) {
      db.deleteObjectStore(LEGACY_EXPENSES_STORE);
    }

    console.log(`[offline/db] Migrated ${expenses.length} legacy expenses to outbox`);
  };

  getAllRequest.onerror = () => {
    console.error("[offline/db] Failed to migrate legacy expenses:", getAllRequest.error);
  };
}
